/**
 * 당겨서 새로고침 (Pull-to-Refresh) — 앱 웹뷰 사이트 공용. 네이버 앱처럼 화면이 같이 내려온다.
 *
 * 문서(window)가 스크롤러인 구조(mobile/layouts/main.blade.php: body{overflow:auto})에 맞춰
 * document 에 touch 이벤트를 걸고, "스크롤 최상단"에서 아래로 당길 때만 개입한다.
 *   - 당김: <body> 를 translateY 로 내려 헤더·콘텐츠 전체를 손가락 따라 밀어냄(네이티브감)
 *   - 인디케이터: <body> 바깥(<html>)에 붙여, 드러난 상단 여백에 그라데이션 스피너 + 문구 표시
 *     (당겨서 새로고침 → 놓으면 새로고침 → 새로운 내용을 불러오고 있어요)
 *   - 임계값 넘겨 놓으면 location.reload() 로 페이지 전체 새로고침
 * (Toast.js 와 동일하게 CSS/DOM 자가주입 — SCSS 파일 수정 불필요)
 *
 * 활성화 — 쓰는 사이트의 부트스트랩이 $j.Ui.PullRefresh.enable(opts) 를 1회 호출한다.
 *
 * 옵션 (기본값 = 문서(window)가 스크롤러인 구조)
 *   scroller    : 실제 스크롤러가 body 가 아니라 내부 요소인 레이아웃 — 그 요소의 CSS 선택자.
 *                 당김·최상단 판정·translate 를 그 요소 기준으로 한다. **지정했는데 페이지에 없으면 켜지 않는다**
 *                 (body 기준으로 조용히 떨어지면 그 레이아웃이 전제하지 않은 화면에서 새로고침이 걸린다)
 *   anchorIndicator : true 면 스피너·문구를 뷰포트 맨 위가 아니라 **scroller 의 윗변**에 맞춘다.
 *                 스크롤러가 헤더 아래에 있는 레이아웃(/m/menu 시트)에서 드러나는 틈과 스피너 위치를 맞추기 위함.
 *                 scroller 없이는 무시. 기본 false(현행 = 뷰포트 맨 위)
 *
 * 다시 켜기 — enable() 은 멱등(_bound)이라 옵션을 바꾸려면 disable() 뒤 enable(). 부트스트랩이 body 기준으로
 *   켜 둔 뒤, 화면 전체가 position:fixed 시트인 페이지(/m/menu)가 내부 스크롤러로 바꿔 켤 때 그렇게 한다
 *   (fixed 상위 요소는 _blockedByAncestor 가 모달로 보고 당김을 막으므로, scroller 를 주지 않으면 그 화면에선 영영 안 켜진다).
 *
 * 색은 컴포넌트 토큰 --ui-ptr-{reveal-bg,ring,ring-soft,ring-clear,text} — 폴백이 기본 모습이고, 사이트 scss 가 :root 에서 잇는다.
 *
 * 충돌 회피 —
 *   - BottomSheet 오픈 시 body 가 position:fixed + .bs-body-locked → 이때는 당김 무시
 *   - 터치 지점 상위에 스크롤이 위로 남은 요소(내부 리스트 등)나 고정 오버레이가 있으면 무시
 *   - 가로 스와이프(Swipe.js) 보호: 세로 우세 제스처일 때만 개입
 */
export default class PullRefresh {
    constructor() {
        this.$el = null;        // 인디케이터(스피너+문구) 엘리먼트(<html> 직속)
        this.$txt = null;
        this._styleInjected = false;
        this._bound = false;
        this._scroller = null;  // enable({scroller}) 로 지정한 스크롤/당김 대상 (없으면 document.body/window)
        this._anchor = false;   // enable({anchorIndicator}) — 인디케이터를 scroller 윗변에 맞출 것인가
        this._baseTop = 0;      // 인디케이터 기준 y(뷰포트 좌표). anchor 일 때 _engage 에서 잰다

        // 제스처 상태
        this._active = false;   // 이번 터치가 당김 후보인가
        this._engaged = false;  // 세로 당김으로 확정되었는가
        this._refreshing = false;
        this._startY = 0;
        this._startX = 0;
        this._dist = 0;
        this._msg = '';

        // 튜닝값
        this.THRESHOLD = 70;    // 이 거리(감쇠 후, px) 이상 당기면 새로고침
        this.MAX_PULL = 150;    // 최대 당김 거리
        this.RESIST = 0.6;      // 당김 감쇠 계수 (손가락 이동 대비)
        this.SLOP = 6;          // 세로/가로 판정 시작 여유

        // 문구
        this.TXT_PULL = '당겨서 새로고침';
        this.TXT_RELEASE = '놓으면 새로고침';
        this.TXT_LOADING = '새로운 내용을 불러오고 있어요.';
        this._msgLoading = this.TXT_LOADING;   // enable({textLoading}) 로 바꿀 수 있다

        this._SPRING = 'transform .32s cubic-bezier(.22,1,.36,1)';
    }

    /* ───────────────── public ───────────────── */

    /** 리스너 바인딩(사이트 부팅 시 1회). 멱등. 옵션은 머리주석. */
    enable = (opts = {}) => {
        if (this._bound) return;
        // 옵션은 매번 새로 정한다 — disable() 뒤 다시 켤 때 이전 옵션이 남지 않게
        this._scroller = opts.scroller ? document.querySelector(opts.scroller) : null;
        if (opts.scroller && !this._scroller) return;
        this._msgLoading = ('textLoading' in opts) ? opts.textLoading : this.TXT_LOADING;
        this._anchor = !!(opts.anchorIndicator && this._scroller);
        this._bound = true;
        this._injectStyle();
        // touchmove 는 preventDefault 를 위해 passive:false 필수
        document.addEventListener('touchstart', this._onStart, { passive: true });
        document.addEventListener('touchmove', this._onMove, { passive: false });
        document.addEventListener('touchend', this._onEnd, { passive: true });
        document.addEventListener('touchcancel', this._onEnd, { passive: true });
    };

    disable = () => {
        if (!this._bound) return;
        this._bound = false;
        document.removeEventListener('touchstart', this._onStart, { passive: true });
        document.removeEventListener('touchmove', this._onMove, { passive: false });
        document.removeEventListener('touchend', this._onEnd, { passive: true });
        document.removeEventListener('touchcancel', this._onEnd, { passive: true });
    };

    destroy = () => this.disable();

    /* ───────────────── handlers ───────────────── */

    _onStart = (e) => {
        this._active = false;
        this._engaged = false;
        if (this._refreshing) return;
        if (e.touches && e.touches.length !== 1) return;        // 멀티터치(핀치 등) 제외
        if (!this._eligible(e.target)) return;

        const t = e.touches[0];
        this._startY = t.clientY;
        this._startX = t.clientX;
        this._dist = 0;
        this._active = true;
    };

    _onMove = (e) => {
        if (!this._active || this._refreshing) return;
        const t = e.touches[0];
        const dy = t.clientY - this._startY;
        const dx = t.clientX - this._startX;

        // 아직 방향 미확정 → 세로/가로 판정
        if (!this._engaged) {
            if (dy <= this.SLOP) {
                // 위로 올리거나 미미한 이동 → 정상 스크롤에 양보하고 이번 제스처 종료
                if (dy < -2) this._active = false;
                return;
            }
            if (Math.abs(dx) > dy) {           // 가로 우세 → 스와이프 등에 양보
                this._active = false;
                return;
            }
            // 도중에 스크롤이 최상단을 벗어났으면(관성 등) 취소
            if (this._scrollTop() > 0) { this._active = false; return; }
            this._engage();
        }

        // 세로 당김 확정 — 페이지 스크롤/네이티브 새로고침 억제하고 화면을 내림
        e.preventDefault();
        this._dist = Math.min(this.MAX_PULL, dy * this.RESIST);
        this._render(this._dist);
    };

    _onEnd = () => {
        if (!this._active) return;
        const engaged = this._engaged;
        const dist = this._dist;
        this._active = false;
        this._engaged = false;
        if (!engaged) return;

        if (dist >= this.THRESHOLD) this._trigger();
        else this._reset();
    };

    /* ───────────────── core ───────────────── */

    /** 세로 당김 확정: 스프링 전이 끄고 손가락에 1:1 추종. */
    _engage() {
        this._engaged = true;
        this._el();                            // 인디케이터 생성 보장
        // body 에 transform 이 걸리면 body 가 position:fixed 자식의 기준 박스가 된다.
        // top:50% 로 앉은 가장자리 탭은 '페이지 전체 높이의 절반'으로 튀어 잔상을 남기므로
        // 당기는 동안 숨긴다 (_edge-tab.scss 맨 아래 숨김 목록). scroller 옵션을 써도
        // CSS 는 body.ptr-pulling 을 보므로 클래스는 항상 body 에 단다.
        document.body.classList.add('ptr-pulling');
        (this._scroller || document.body).style.transition = 'none';
        this.$el.classList.remove('is-refreshing');
        this._baseTop = this._anchor ? this._scrollerTop() : 0;
    }

    /** scroller 윗변의 뷰포트 y — 직전 _reset 의 스프링(translateY 복귀 중)이 남아 있어도 원위치 기준으로 잰다. */
    _scrollerTop() {
        const el = this._scroller;
        const rect = el.getBoundingClientRect();
        let shift = 0;
        try {
            const tf = getComputedStyle(el).transform;
            if (tf && tf !== 'none') shift = new DOMMatrix(tf).m42 || 0;
        } catch (e) { /* DOMMatrix 미지원 구형 웹뷰 — 보정 없이 진행 */ }
        return Math.max(0, rect.top - shift);
    }

    _render(dist) {
        // 화면 전체(헤더+콘텐츠, 또는 지정된 스크롤 대상)를 손가락 따라 내림
        (this._scroller || document.body).style.transform = `translateY(${dist}px)`;

        // 드러난 상단 여백 중앙에 인디케이터 배치 + 진행도 반영
        const p = Math.min(1, dist / this.THRESHOLD);
        const y = Math.max(6, dist / 2 - 13);   // 여백 세로 중앙
        const el = this.$el;
        el.style.opacity = String(p);
        el.style.transform = `translateX(-50%) translateY(${this._baseTop + y}px)`;
        this._setMsg(dist >= this.THRESHOLD ? this.TXT_RELEASE : this.TXT_PULL);

        const ring = el.firstElementChild;
        if (ring) ring.style.transform = `rotate(${dist * 2.6}deg)`;
    }

    _trigger() {
        this._refreshing = true;
        // 화면을 임계 위치에 물려두고 스피너 회전 + 로딩 문구 → 곧바로 새로고침
        const el0 = this._scroller || document.body;
        el0.style.transition = this._SPRING;
        el0.style.transform = `translateY(${this.THRESHOLD}px)`;
        const el = this._el();
        el.style.opacity = '1';
        el.style.transform = `translateX(-50%) translateY(${this._baseTop + this.THRESHOLD / 2 - 13}px)`;
        el.classList.add('is-refreshing');
        this._setMsg(this._msgLoading);
        // 스냅다운·문구 노출 한 박자 후 새로고침(스피너는 리로드까지 유지)
        setTimeout(() => { location.reload(); }, 320);
    }

    _reset() {
        const el0 = this._scroller || document.body;
        el0.style.transition = this._SPRING;
        el0.style.transform = 'translateY(0)';
        const el = this._el();
        el.style.opacity = '0';
        // 전이 종료 후 인라인 스타일 정리(다른 코드의 transform 오염 방지)
        setTimeout(() => {
            if (this._refreshing) return;          // 그새 새 당김이 시작되면 건드리지 않음
            el0.style.transition = '';
            el0.style.transform = '';
            if (!this._engaged) document.body.classList.remove('ptr-pulling');
        }, 360);
    }

    /* ───────────────── eligibility ───────────────── */

    /** 지금 당김을 시작해도 되는 상태인가. */
    _eligible(target) {
        // BottomSheet/모달 등으로 body 가 잠긴 경우 제외
        if (document.body.classList.contains('bs-body-locked')) return false;
        if (getComputedStyle(document.body).position === 'fixed') return false;
        // 문서 스크롤이 최상단이어야 함
        if (this._scrollTop() > 0) return false;
        // 터치 지점 상위에 아직 위로 스크롤 가능한 요소/고정 오버레이가 있으면 제외
        return !this._blockedByAncestor(target);
    }

    _blockedByAncestor(node) {
        const stopAt = this._scroller || document.body;
        let el = node;
        while (el && el !== stopAt && el.nodeType === 1) {
            const cs = getComputedStyle(el);
            // 고정/스티키 오버레이(모달·시트 등) 위에서는 당김 금지
            if (cs.position === 'fixed' || cs.position === 'sticky') return true;
            // 내부 스크롤 영역이 최상단이 아니면(위로 더 스크롤 가능) 당김 금지
            const oy = cs.overflowY;
            if ((oy === 'auto' || oy === 'scroll') &&
                el.scrollHeight > el.clientHeight && el.scrollTop > 0) return true;
            el = el.parentElement;
        }
        return false;
    }

    _scrollTop() {
        if (this._scroller) return this._scroller.scrollTop || 0;
        return window.pageYOffset ||
            document.documentElement.scrollTop ||
            document.body.scrollTop || 0;
    }

    /* ───────────────── dom / css ───────────────── */

    _setMsg(msg) {
        if (this._msg === msg) return;
        this._msg = msg;
        if (this.$txt) this.$txt.textContent = msg;
    }

    /** 인디케이터 생성/획득. body 를 translate 하므로 <html> 직속(=body 밖)에 둔다. */
    _el() {
        this._injectStyle();
        if (!this.$el) {
            const el = document.createElement('div');
            el.className = 'jb-ptr';
            el.setAttribute('aria-hidden', 'true');
            el.innerHTML = '<span class="jb-ptr-ring"></span><span class="jb-ptr-txt"></span>';
            document.documentElement.appendChild(el);   // <html> 직속 → body transform 영향 없음
            this.$el = el;
            this.$txt = el.querySelector('.jb-ptr-txt');
        }
        return this.$el;
    }

    _injectStyle() {
        if (this._styleInjected) return;
        this._styleInjected = true;
        // 네이티브(크롬) 당겨서새로고침 억제 + 인디케이터 스타일. body 는 원점 기준 transform.
        // 색은 컴포넌트 토큰 --ui-ptr-* — 폴백이 기본 모습이고, 사이트 scss 가 :root 에서 잇는다.
        // (:root 에 기본값을 선언하지 않는다 — 이 CSS 는 사이트 CSS 뒤에 주입돼 같은 특이도의 사이트 매핑을 이겨 버린다)
        const css = `
html, body { overscroll-behavior-y: contain; }
html { background:var(--ui-ptr-reveal-bg, #fff); }   /* body 를 내릴 때 드러나는 상단 여백 색 맞춤 */
.jb-ptr{position:fixed;left:50%;top:calc(env(safe-area-inset-top,0px) + 0px);
  transform:translateX(-50%) translateY(6px);opacity:0;
  z-index:var(--z-index-popover,1065);pointer-events:none;
  display:flex;align-items:center;gap:9px;white-space:nowrap}
.jb-ptr-ring{flex:0 0 auto;width:22px;height:22px;border-radius:50%;
  background:conic-gradient(from 0deg,
    var(--ui-ptr-ring-clear, rgba(22,163,74,0)) 0%, var(--ui-ptr-ring-soft, rgba(22,163,74,.15)) 25%, var(--ui-ptr-ring, #16a34a) 100%);
  -webkit-mask:radial-gradient(farthest-side,transparent calc(100% - 3px),#000 calc(100% - 3px));
          mask:radial-gradient(farthest-side,transparent calc(100% - 3px),#000 calc(100% - 3px))}
.jb-ptr-txt{font-size:15px;font-weight:600;color:var(--ui-ptr-text, #333);line-height:1}
.jb-ptr.is-refreshing .jb-ptr-ring{animation:jb-ptr-rotate .8s linear infinite}
@keyframes jb-ptr-rotate{to{transform:rotate(360deg)}}
`;
        const style = document.createElement('style');
        style.textContent = css;
        document.head.appendChild(style);
    }
}
